"use client";

import { useRef } from "react";
import Image from "next/image";

import { Container } from "@/src/components/ui/Container";
import Section from "@/src/components/ui/Section";
import { Heading, Text } from "@/src/components/ui/Typography";
import { StickyColumnUntilLevel } from "@/src/components/molecules/StickyColumnUntilLevel";

import type { ProposedSolutionItemData } from "./ProposedSolution.types";

interface ProposedSolutionProps {
  items: ProposedSolutionItemData[];
}

export function ProposedSolution({ items }: ProposedSolutionProps) {
  const boundsRef = useRef<HTMLDivElement>(null);

  if (items.length === 0) return null;

  return (
    <Section className="bg-white">
      <Container>
        <div className="grid gap-6 md:grid-cols-2 md:gap-24">
          <StickyColumnUntilLevel
            boundsRef={boundsRef}
            className="h-fit self-start"
          >
            <Heading as="h2" size="displayLG">
              Proposed Solution
            </Heading>
          </StickyColumnUntilLevel>

          <div ref={boundsRef} className="flex flex-col gap-8 md:gap-23">
            {items.map((item, index) => (
              <div key={index}>
                <Heading as="h3" size="headingLG" className="text-neutral-700">
                  {item.title}
                </Heading>

                <Text size="bodySM" className="mt-4 text-neutral-700">
                  {item.description}
                </Text>

                {item.image && (
                  <div className="relative mt-8 aspect-video w-full overflow-hidden rounded-(--radius-xl) bg-neutral-100 border border-borderGray">
                    <Image
                      src={item.image}
                      alt={item.imageAlt}
                      fill
                      className="object-cover"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Container>
    </Section>
  );
}
